// components/TakeoffEstimateTools.jsx
// The estimate page's helpers and pop-ups:
//   ItemSearchModal   trade-aware item search (lib/takeoff/itemSearch.js) — chained items come in with their item
//   WasteModal        set / adjust waste % on one assembly, the selected items, the bid area in view or everything
//   LibraryModal      standard assemblies: build them, import them into one or many assemblies, save one as a standard
//   PrintModal        the printed estimate / client proposal (lib/takeoff/estimateReport.js)
// + itemFromCatalog / expandChain (catalog item → estimate item, with its chained items)

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  resolveDefaultRate,
  defaultCalcMode,
} from "../lib/takeoff/estimateCalc";
import { smartSearch, SEARCH_CHIPS } from "../lib/takeoff/itemSearch";
import { PRINT_SECTIONS, PRINT_PRESETS } from "../lib/takeoff/estimateReport";

export const cls = (...c) => c.filter(Boolean).join(" ");
export const uid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
export const inp =
  "w-full rounded-md border border-gray-700 bg-gray-950 px-1.5 py-1 text-[12px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none";
const inp2 =
  "w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-[13px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none";
const MATERIAL = "#f97316";
const LABOUR = "#38bdf8";
const money2 = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const MODES = [
  ["auto", "Auto"],
  ["takeoff", "Takeoff qty"],
  ["sf", "Area (SF)"],
  ["lf", "Length (LF)"],
  ["pieces", "Pieces (coverage)"],
  ["oc", "Studs O.C."],
  ["ea", "Each (count)"],
];
const KINDS = [
  ["wall", "Wall"],
  ["ceiling", "Ceiling"],
  ["soffit", "Soffit / bulkhead"],
  ["floor", "Floor"],
  ["other", "Other"],
];

// ── catalog item → estimate item ─────────────────────────────────────────────
export function itemFromCatalog(db, { cond = null, ccIndex = null } = {}) {
  const cov = parseFloat(db.coveragePerUnit);
  const ccId = db.costCode?._id || db.costCode || null;
  const cc = ccId && ccIndex ? ccIndex.get(String(ccId)) : null;
  return {
    id: uid(),
    itemCode: db.itemCode,
    title: db.description || db.title || "",
    uom: db.uom || "",
    category: db.category || "",
    sectionNumber: db.sectionNumber || "",
    itemDatabaseId: db._id || null,
    costCode: ccId ? String(ccId) : null,
    costCodeLabel: cc ? [cc.code, cc.name].filter(Boolean).join(" – ") : "",
    calcMode: defaultCalcMode(cond, db),
    ocInches: "",
    ocDir: "v",
    size: db.size || "",
    coveragePerUnit: cov > 0 ? cov : null,
    covUnit: "",
    productionRate:
      db.category === "L" ? Number(db.productionRate) || null : null,
    layers: 1,
    multiplier: 1,
    wastePct: 0,
    rate: resolveDefaultRate(db),
  };
}

// a catalog item + everything chained to it (linkedItems, followed a few levels deep, each item once)
// → [{ db, link, depth }]  (link = { factor, calcMode, wastePct, note } of the chain that brought it in)
export function expandChain(root, catalogById, maxDepth = 4) {
  const out = [];
  const seen = new Set();
  const walk = (db, link, depth) => {
    const key = String(db._id || db.itemCode);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ db, link, depth });
    if (depth >= maxDepth) return;
    for (const l of db.linkedItems || []) {
      const child = catalogById?.get(String(l.item?._id || l.item));
      if (child) walk(child, l, depth + 1);
    }
  };
  walk(root, null, 0);
  return out;
}

// the chained items of a link applied to the new estimate item
export function applyLink(item, link) {
  if (!link) return item;
  return {
    ...item,
    multiplier: Number(link.factor) > 0 ? Number(link.factor) : 1,
    ...(link.calcMode ? { calcMode: link.calcMode } : {}),
    ...(link.wastePct != null && link.wastePct !== ""
      ? { wastePct: Number(link.wastePct) || 0 }
      : {}),
  };
}

// estimate item → standard-assembly item (no ids / refs; the catalog link stays)
export function toTemplateItem(i) {
  const { id: _id, ref: _r, chainOf: _c, ...rest } = i;
  return rest;
}

// ── shell ────────────────────────────────────────────────────────────────────
function Modal({
  open,
  onClose,
  children,
  width = "max-w-2xl",
  z = "z-[2000]",
  top = "pt-[7vh]",
}) {
  useEffect(() => {
    if (!open) return;
    const k = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className={cls(
        "fixed inset-0 flex items-start justify-center bg-black/70 p-4",
        z,
        top,
      )}
      onMouseDown={onClose}
    >
      <div
        className={cls(
          "w-full overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl",
          width,
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
function Head({ title, sub, onClose, children }) {
  return (
    <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white">{title}</div>
        {sub && <div className="truncate text-[11px] text-gray-500">{sub}</div>}
      </div>
      <div className="ml-auto flex items-center gap-2">
        {children}
        <button
          onClick={onClose}
          className="rounded-lg px-2 py-1 text-gray-400 hover:bg-white/10 hover:text-white"
          title="Close (Esc)"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
function Lbl({ children, className }) {
  return (
    <span
      className={cls(
        "mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500",
        className,
      )}
    >
      {children}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ITEM SEARCH
// ═════════════════════════════════════════════════════════════════════════════
export function ItemSearchModal({
  open,
  onClose,
  onSelect, // (dbItem, { chain: bool }) → void | string (message)
  catalog,
  loading,
  initialQ = "",
  title = "Add item from database",
  sub = "",
  z,
}) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [hi, setHi] = useState(0);
  const [chain, setChain] = useState(() => {
    try {
      return localStorage.getItem("est.chain") !== "0";
    } catch {
      return true;
    }
  });
  const [keepOpen, setKeepOpen] = useState(false);
  const [flash, setFlash] = useState("");
  const ref = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    if (open) {
      setQ(initialQ || "");
      setHi(0);
      setFlash("");
      setTimeout(() => ref.current?.focus(), 40);
    }
  }, [open, initialQ]);
  useEffect(() => {
    try {
      localStorage.setItem("est.chain", chain ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, [chain]);
  const byId = useMemo(
    () => new Map((catalog || []).map((c) => [String(c._id), c])),
    [catalog],
  );
  const results = useMemo(
    () => smartSearch(catalog || [], q, { category: cat, limit: 150 }),
    [catalog, q, cat],
  );
  useEffect(() => setHi(0), [q, cat]);
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-i="${hi}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [hi]);
  if (!open) return null;
  const pick = (i) => {
    const r = onSelect(i, { chain });
    if (!keepOpen) onClose();
    else {
      const n = chain
        ? (i.linkedItems || []).filter((l) =>
            byId.has(String(l.item?._id || l.item)),
          ).length
        : 0;
      setFlash(
        typeof r === "string"
          ? r
          : `Added ${i.itemCode}${n ? ` + ${n} chained` : ""}`,
      );
      setTimeout(() => setFlash(""), 2200);
      ref.current?.focus();
      ref.current?.select();
    }
  };
  const firstPartial = results.findIndex((r) => r._partial);
  const addChip = (w) =>
    setQ((p) => (p.toLowerCase().includes(w) ? p : `${p.trim()} ${w}`.trim()));
  return (
    <Modal open={open} onClose={onClose} z={z}>
      <Head title={title} sub={sub} onClose={onClose} />
      <div className="space-y-2 border-b border-white/10 p-3">
        <div className="flex items-center gap-2">
          <input
            ref={ref}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHi((h) => Math.min(h + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHi((h) => Math.max(h - 1, 0));
              } else if (e.key === "Enter" && results[hi]) {
                e.preventDefault();
                pick(results[hi]);
              }
            }}
            placeholder={`Try: 5/8 x 4x10 · 3-5/8 20ga stud · 362S125-33 · hat channel · corner bead…`}
            className="flex-1 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
          />
          {[
            ["all", "All"],
            ["M", "Material"],
            ["L", "Labour"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setCat(k)}
              className={cls(
                "rounded-lg px-2.5 py-1.5 text-xs font-medium",
                cat === k
                  ? "bg-orange-500 text-white"
                  : "bg-white/5 text-gray-300 hover:bg-white/10",
              )}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {SEARCH_CHIPS.map(([l, w]) => (
            <button
              key={l}
              onClick={() => {
                addChip(w);
                ref.current?.focus();
              }}
              className="rounded-full bg-white/5 px-2 py-0.5 text-[10.5px] text-gray-400 hover:bg-orange-500/15 hover:text-orange-200"
            >
              {l}
            </button>
          ))}
          {q && (
            <button
              onClick={() => setQ("")}
              className="ml-1 text-[10.5px] text-gray-500 hover:text-gray-300"
            >
              clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-400">
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={chain}
              onChange={(e) => setChain(e.target.checked)}
              className="accent-orange-500"
            />
            Bring in chained items{" "}
            <span className="text-gray-600">
              (⛓ set on the item in the item database)
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={keepOpen}
              onChange={(e) => setKeepOpen(e.target.checked)}
              className="accent-orange-500"
            />
            Keep open to add more
          </label>
          <span className="ml-auto text-gray-600">
            ↑↓ to move · Enter to add
          </span>
        </div>
        {flash && (
          <div className="rounded-md bg-emerald-500/10 px-2 py-1 text-[11.5px] text-emerald-300">
            ✓ {flash}
          </div>
        )}
      </div>
      <div ref={listRef} className="max-h-[52vh] overflow-y-auto">
        {loading && (
          <p className="p-6 text-center text-xs text-gray-500">
            Loading catalog…
          </p>
        )}
        {!loading && results.length === 0 && (
          <p className="p-6 text-center text-xs text-gray-500">
            No items match. Try fewer words, a size (5/8, 3-5/8), a gauge (20ga
            / 33mil) or a code.
          </p>
        )}
        {results.map((i, idx) => {
          const rate = resolveDefaultRate(i);
          const isL = i.category === "L";
          const links = (i.linkedItems || []).filter((l) =>
            byId.has(String(l.item?._id || l.item)),
          );
          return (
            <React.Fragment key={i._id || i.itemCode}>
              {idx === firstPartial && (
                <div className="bg-black/30 px-4 py-1 text-[10px] uppercase tracking-wider text-gray-500">
                  Close matches (one word didn't match)
                </div>
              )}
              <button
                data-i={idx}
                onMouseEnter={() => setHi(idx)}
                onClick={() => pick(i)}
                className={cls(
                  "flex w-full items-center gap-3 border-b border-white/5 px-4 py-2 text-left",
                  idx === hi ? "bg-orange-500/10" : "hover:bg-white/5",
                  i._partial && "opacity-70",
                )}
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: isL ? LABOUR : MATERIAL }}
                />
                <span className="w-28 shrink-0 truncate font-mono text-[11px] text-gray-400">
                  {i.itemCode}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-gray-100">
                    {i.description || i.title}
                    {i.size ? (
                      <span className="ml-1 text-gray-500">· {i.size}</span>
                    ) : null}
                  </span>
                  {links.length > 0 && (
                    <span className="block truncate text-[10.5px] text-amber-300/80">
                      ⛓ brings in{" "}
                      {links
                        .slice(0, 3)
                        .map(
                          (l) =>
                            byId.get(String(l.item?._id || l.item))?.itemCode,
                        )
                        .join(", ")}
                      {links.length > 3 ? ` +${links.length - 3}` : ""}
                    </span>
                  )}
                </span>
                {isL && Number(i.productionRate) > 0 && (
                  <span className="shrink-0 text-[10px] text-sky-300">
                    {i.productionRate}/hr
                  </span>
                )}
                {Number(i.coveragePerUnit) > 0 && (
                  <span className="shrink-0 text-[10px] text-orange-300">
                    cov {i.coveragePerUnit}
                  </span>
                )}
                <span className="w-10 shrink-0 text-right text-[11px] text-gray-500">
                  {i.uom}
                </span>
                <span className="w-16 shrink-0 text-right text-xs text-gray-300">
                  {rate ? money2(rate) : ""}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </Modal>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// WASTE %
// ═════════════════════════════════════════════════════════════════════════════
// scopes: [{ key, label }] ; countFor(scopeKey, cat) → number of items it would change
export function WasteModal({
  open,
  onClose,
  scopes = [],
  defaultScope,
  countFor,
  onApply,
}) {
  const [scope, setScope] = useState(defaultScope);
  const [cat, setCat] = useState("M");
  const [mode, setMode] = useState("set");
  const [value, setValue] = useState(10);
  useEffect(() => {
    if (open) setScope(defaultScope || scopes[0]?.key);
  }, [open, defaultScope]); // eslint-disable-line
  const n = open && scope ? countFor(scope, cat) : 0;
  const v = Number(value);
  const ok =
    value !== "" && Number.isFinite(v) && n > 0 && (mode !== "set" || v >= 0);
  return (
    <Modal open={open} onClose={onClose} width="max-w-md" top="pt-[12vh]">
      <Head
        title="Waste %"
        sub="Change the waste allowance on many items at once (Undo puts it back)"
        onClose={onClose}
      />
      <div className="space-y-4 p-4">
        <div>
          <Lbl>Apply to</Lbl>
          <div className="space-y-1">
            {scopes.map((s) => (
              <label
                key={s.key}
                className="flex cursor-pointer items-center gap-2 text-[13px] text-gray-200"
              >
                <input
                  type="radio"
                  name="wscope"
                  checked={scope === s.key}
                  onChange={() => setScope(s.key)}
                  className="accent-orange-500"
                />
                {s.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Lbl>Items</Lbl>
          <div className="inline-flex rounded-lg bg-gray-950 p-0.5 text-xs">
            {[
              ["M", "Material only"],
              ["L", "Labour only"],
              ["all", "Everything"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setCat(k)}
                className={cls(
                  "rounded-md px-3 py-1 font-medium",
                  cat === k
                    ? "bg-orange-500 text-white"
                    : "text-gray-400 hover:text-gray-200",
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Lbl>How</Lbl>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className={cls(inp2, "w-40")}
            >
              <option value="set">Set waste to</option>
              <option value="add">Add / subtract</option>
            </select>
          </div>
          <div>
            <Lbl>{mode === "set" ? "Waste %" : "± points"}</Lbl>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={cls(inp2, "w-24 text-right")}
                autoFocus
              />
              <span className="text-xs text-gray-500">%</span>
            </div>
          </div>
          <div className="flex gap-1 pb-0.5">
            {[0, 5, 10, 15].map((p) => (
              <button
                key={p}
                onClick={() => {
                  setMode("set");
                  setValue(p);
                }}
                className="rounded-md bg-white/5 px-2 py-1 text-[11px] text-gray-300 hover:bg-white/10"
              >
                {p}%
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 pt-3">
          <span className="text-[12px] text-gray-400">
            {n} item{n === 1 ? "" : "s"} will change
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
            >
              Cancel
            </button>
            <button
              disabled={!ok}
              onClick={() => onApply({ scope, cat, mode, value: v })}
              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STANDARD ASSEMBLIES
// ═════════════════════════════════════════════════════════════════════════════
const blankTemplate = () => ({
  name: "",
  kind: "wall",
  code: "",
  description: "",
  notes: "",
  height: "",
  items: [],
});

export function LibraryModal({
  open,
  onClose,
  api, // { list, save, remove, duplicate, used }
  catalog,
  catLoading,
  catalogById,
  ccIndex,
  conditions = [], // [{ id, name, color, itemCount }]
  target = null, // condition id the library was opened from
  seed = null, // a new standard to start editing ("Save as standard")
  onImport, // (template, { targets: [cid], mode: "append"|"replace", refresh: bool }) → message
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null); // editing
  const [busy, setBusy] = useState(false);
  const [targets, setTargets] = useState(() => new Set());
  const [mode, setMode] = useState("append");
  const [refresh, setRefresh] = useState(true);
  const [tq, setTq] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    if (!api?.list) return;
    setLoading(true);
    setErr("");
    try {
      setList((await api.list()) || []);
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          e.message ||
          "Could not load the standard assemblies",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!open) return;
    load();
    setNote("");
    setTargets(new Set(target ? [target] : []));
    setDraft(seed ? { ...blankTemplate(), ...seed } : null);
    setSelId(null);
    setMode("append");
  }, [open]); // eslint-disable-line

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return list
      .filter((x) => kind === "all" || x.kind === kind)
      .filter(
        (x) =>
          !t ||
          `${x.name} ${x.code} ${x.description} ${(x.tags || []).join(" ")} ${(x.items || []).map((i) => i.itemCode + " " + i.title).join(" ")}`
            .toLowerCase()
            .includes(t),
      );
  }, [list, q, kind]);
  const sel = list.find((x) => x._id === selId) || null;

  const save = async () => {
    if (!draft.name.trim()) return setErr("Give it a name");
    setBusy(true);
    setErr("");
    try {
      const saved = await api.save({
        ...draft,
        height: draft.height === "" ? null : Number(draft.height),
      });
      await load();
      setSelId(saved?._id || null);
      setDraft(null);
      setNote(`Saved “${saved?.name || draft.name}”`);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  const del = async (t) => {
    if (
      !window.confirm(
        `Delete the standard assembly “${t.name}”? Estimates that used it keep their items.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.remove(t._id);
      setSelId(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };
  const dup = async (t) => {
    setBusy(true);
    try {
      const d = await api.duplicate(t._id);
      await load();
      setSelId(d?._id || null);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Duplicate failed");
    } finally {
      setBusy(false);
    }
  };
  const doImport = () => {
    if (!sel || !targets.size) return;
    const msg = onImport(sel, { targets: [...targets], mode, refresh });
    api.used?.(sel._id, targets.size);
    setNote(msg || `Imported “${sel.name}”`);
    if (target && targets.size === 1) onClose();
  };

  // ── editor helpers ──
  const setD = (p) => setDraft((d) => ({ ...d, ...p }));
  const setItem = (k, p) =>
    setDraft((d) => ({
      ...d,
      items: d.items.map((it, i) => (i === k ? { ...it, ...p } : it)),
    }));
  const move = (k, dir) =>
    setDraft((d) => {
      const items = [...d.items];
      const j = k + dir;
      if (j < 0 || j >= items.length) return d;
      [items[k], items[j]] = [items[j], items[k]];
      return { ...d, items };
    });
  const addFromCatalog = (db, { chain }) => {
    const chainList = chain
      ? expandChain(db, catalogById)
      : [{ db, link: null }];
    let added = 0;
    setDraft((d) => {
      const have = new Set(
        d.items.map((i) => String(i.itemDatabaseId || i.itemCode)),
      );
      const extra = [];
      chainList.forEach(({ db: x, link }, k) => {
        const key = String(x._id || x.itemCode);
        if (k > 0 && have.has(key)) return; // a chained item already in the standard
        extra.push(
          toTemplateItem(applyLink(itemFromCatalog(x, { ccIndex }), link)),
        );
        have.add(key);
        added++;
      });
      return { ...d, items: [...d.items, ...extra] };
    });
    return `Added ${db.itemCode}${chainList.length > 1 ? ` + ${chainList.length - 1} chained` : ""}`;
  };

  const tShown = conditions.filter(
    (c) => !tq.trim() || c.name.toLowerCase().includes(tq.trim().toLowerCase()),
  );

  return (
    <>
      <Modal open={open} onClose={onClose} width="max-w-6xl" top="pt-[4vh]">
        <Head
          title="Standard assemblies"
          sub="Build your typical wall / ceiling build-ups once, then bring them into any estimate"
          onClose={onClose}
        >
          {note && <span className="text-xs text-emerald-300">✓ {note}</span>}
          {!draft && (
            <button
              onClick={() => {
                setDraft(blankTemplate());
                setSelId(null);
              }}
              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
            >
              + New standard
            </button>
          )}
        </Head>
        {err && (
          <div className="border-b border-red-900/50 bg-red-950/40 px-4 py-2 text-xs text-red-300">
            {err}
          </div>
        )}
        <div className="grid h-[78vh] grid-cols-1 md:grid-cols-[300px_1fr]">
          {/* list */}
          <div className="flex min-h-0 flex-col border-r border-white/10">
            <div className="space-y-2 border-b border-white/10 p-3">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, tag, item…"
                className={inp2}
              />
              <div className="flex flex-wrap gap-1">
                {[["all", "All"], ...KINDS].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    className={cls(
                      "rounded-full px-2 py-0.5 text-[10.5px]",
                      kind === k
                        ? "bg-orange-500 text-white"
                        : "bg-white/5 text-gray-400 hover:bg-white/10",
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading && <p className="p-4 text-xs text-gray-500">Loading…</p>}
              {!loading && !shown.length && (
                <p className="p-4 text-xs text-gray-500">
                  {list.length
                    ? "Nothing matches."
                    : "No standard assemblies yet — click “+ New standard”, or use “Save as standard” on a priced assembly."}
                </p>
              )}
              {shown.map((t) => (
                <button
                  key={t._id}
                  onClick={() => {
                    setSelId(t._id);
                    setDraft(null);
                  }}
                  className={cls(
                    "block w-full border-b border-white/5 px-3 py-2.5 text-left",
                    selId === t._id && !draft
                      ? "bg-orange-500/10"
                      : "hover:bg-white/5",
                  )}
                >
                  <div className="flex items-center gap-2">
                    {t.code && (
                      <span className="rounded bg-white/10 px-1.5 py-px font-mono text-[10px] text-gray-300">
                        {t.code}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-100">
                      {t.name}
                    </span>
                  </div>
                  <div className="mt-0.5 flex gap-2 text-[10.5px] text-gray-500">
                    <span className="capitalize">{t.kind}</span>·
                    <span>{(t.items || []).length} items</span>
                    {t.uses > 0 && <span>· used {t.uses}×</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* detail / editor */}
          <div className="min-h-0 overflow-y-auto p-4">
            {draft ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                  <label className="col-span-2 md:col-span-3">
                    <Lbl>Name *</Lbl>
                    <input
                      value={draft.name}
                      onChange={(e) => setD({ name: e.target.value })}
                      placeholder={`e.g. 5/8" Type X both sides · 3-5/8" 20ga @ 16"`}
                      className={inp2}
                      autoFocus
                    />
                  </label>
                  <label>
                    <Lbl>Type</Lbl>
                    <select
                      value={draft.kind}
                      onChange={(e) => setD({ kind: e.target.value })}
                      className={inp2}
                    >
                      {KINDS.map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <Lbl>Tag / code</Lbl>
                    <input
                      value={draft.code}
                      onChange={(e) => setD({ code: e.target.value })}
                      placeholder="W1A"
                      className={inp2}
                    />
                  </label>
                  <label>
                    <Lbl>Typical height (ft)</Lbl>
                    <input
                      type="number"
                      step="any"
                      value={draft.height ?? ""}
                      onChange={(e) => setD({ height: e.target.value })}
                      className={inp2}
                    />
                  </label>
                  <label className="col-span-2 md:col-span-3">
                    <Lbl>Build-up / description</Lbl>
                    <textarea
                      rows={2}
                      value={draft.description}
                      onChange={(e) => setD({ description: e.target.value })}
                      className={inp2}
                    />
                  </label>
                  <label className="col-span-2 md:col-span-3">
                    <Lbl>Scope notes (copied onto the assembly)</Lbl>
                    <textarea
                      rows={2}
                      value={draft.notes}
                      onChange={(e) => setD({ notes: e.target.value })}
                      className={inp2}
                    />
                  </label>
                </div>
                <div className="overflow-hidden rounded-xl border border-white/10">
                  <div className="grid grid-cols-12 gap-2 bg-black/30 px-3 py-1.5 text-[9.5px] font-semibold uppercase tracking-wider text-gray-500">
                    <div className="col-span-4">Item</div>
                    <div className="col-span-2">Quantity basis</div>
                    <div className="col-span-1">O.C.</div>
                    <div className="col-span-1">Layers</div>
                    <div className="col-span-1">Factor ×</div>
                    <div className="col-span-1">Waste %</div>
                    <div className="col-span-2" />
                  </div>
                  {!draft.items.length && (
                    <p className="p-4 text-center text-xs text-gray-500">
                      No items yet.
                    </p>
                  )}
                  {draft.items.map((it, k) => (
                    <div
                      key={k}
                      className="grid grid-cols-12 items-center gap-2 border-t border-white/5 px-3 py-1.5"
                    >
                      <div className="col-span-4 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{
                              background:
                                it.category === "L" ? LABOUR : MATERIAL,
                            }}
                          />
                          <span className="truncate text-[12.5px] text-gray-100">
                            {it.title || it.itemCode}
                          </span>
                        </div>
                        <div className="truncate pl-3.5 text-[10.5px] text-gray-500">
                          {it.itemCode} · {it.uom}
                          {it.size ? ` · ${it.size}` : ""}
                        </div>
                      </div>
                      <select
                        value={it.calcMode || "auto"}
                        onChange={(e) =>
                          setItem(k, { calcMode: e.target.value })
                        }
                        className={cls(inp, "col-span-2")}
                      >
                        {MODES.map(([m, l]) => (
                          <option key={m} value={m}>
                            {l}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        disabled={it.calcMode !== "oc"}
                        value={it.ocInches ?? ""}
                        onChange={(e) =>
                          setItem(k, {
                            ocInches:
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                          })
                        }
                        placeholder="16"
                        className={cls(inp, "col-span-1 disabled:opacity-30")}
                      />
                      <input
                        type="number"
                        min="1"
                        value={it.layers ?? 1}
                        onChange={(e) =>
                          setItem(k, { layers: Number(e.target.value) || 1 })
                        }
                        className={cls(inp, "col-span-1")}
                      />
                      <input
                        type="number"
                        step="any"
                        value={it.multiplier ?? 1}
                        onChange={(e) =>
                          setItem(k, {
                            multiplier:
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                          })
                        }
                        className={cls(inp, "col-span-1")}
                      />
                      <input
                        type="number"
                        step="any"
                        value={it.wastePct ?? 0}
                        onChange={(e) =>
                          setItem(k, {
                            wastePct:
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                          })
                        }
                        className={cls(inp, "col-span-1")}
                      />
                      <div className="col-span-2 flex justify-end gap-1">
                        <button
                          onClick={() => move(k, -1)}
                          className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-gray-400 hover:bg-white/10"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => move(k, 1)}
                          className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-gray-400 hover:bg-white/10"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() =>
                            setDraft((d) => ({
                              ...d,
                              items: d.items.filter((_, i) => i !== k),
                            }))
                          }
                          className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-gray-400 hover:bg-red-500/20 hover:text-red-300"
                          title="Remove"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-white/5 px-3 py-2">
                    <button
                      onClick={() => setSearchOpen(true)}
                      className="rounded-lg bg-orange-500/15 px-3 py-1.5 text-xs font-semibold text-orange-300 hover:bg-orange-500/25"
                    >
                      + Add item from database
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setDraft(null)}
                    className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={save}
                    disabled={busy}
                    className="rounded-lg bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-50"
                  >
                    {busy
                      ? "Saving…"
                      : draft._id
                        ? "Save changes"
                        : "Save standard"}
                  </button>
                </div>
              </div>
            ) : sel ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {sel.code && (
                        <span className="rounded bg-white/10 px-1.5 py-px font-mono text-[11px] text-gray-300">
                          {sel.code}
                        </span>
                      )}
                      <h3 className="text-base font-semibold text-white">
                        {sel.name}
                      </h3>
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      <span className="capitalize">{sel.kind}</span>
                      {sel.height ? ` · ${sel.height}′ typical` : ""}
                      {sel.updatedByName
                        ? ` · last edited by ${sel.updatedByName}`
                        : ""}
                      {sel.updatedAt
                        ? ` ${new Date(sel.updatedAt).toLocaleDateString()}`
                        : ""}
                    </div>
                    {sel.description && (
                      <p className="mt-2 whitespace-pre-line text-[12.5px] text-gray-300">
                        {sel.description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() =>
                        setDraft({
                          ...sel,
                          height: sel.height ?? "",
                          items: (sel.items || []).map((i) => ({ ...i })),
                        })
                      }
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => dup(sel)}
                      disabled={busy}
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => del(sel)}
                      disabled={busy}
                      className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-white/10">
                  {(sel.items || []).map((it, k) => {
                    const db = it.itemDatabaseId
                      ? catalogById?.get(String(it.itemDatabaseId))
                      : null;
                    const now = db ? resolveDefaultRate(db) : null;
                    return (
                      <div
                        key={k}
                        className="flex items-center gap-3 border-t border-white/5 px-3 py-1.5 first:border-t-0"
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{
                            background: it.category === "L" ? LABOUR : MATERIAL,
                          }}
                        />
                        <span className="w-28 shrink-0 truncate font-mono text-[10.5px] text-gray-500">
                          {it.itemCode}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-100">
                          {it.title}
                        </span>
                        <span className="text-[10.5px] text-gray-500">
                          {MODES.find((m) => m[0] === it.calcMode)?.[1] ||
                            it.calcMode}
                        </span>
                        {Number(it.multiplier) !== 1 && (
                          <span className="text-[10.5px] text-gray-400">
                            ×{it.multiplier}
                          </span>
                        )}
                        {Number(it.wastePct) > 0 && (
                          <span className="text-[10.5px] text-amber-300">
                            +{it.wastePct}%
                          </span>
                        )}
                        <span
                          className="w-20 text-right text-[11px] text-gray-300"
                          title={
                            db
                              ? "today's catalog price"
                              : "price saved with the standard (item no longer in the catalog)"
                          }
                        >
                          {money2(now ?? it.rate)}
                          {!db && it.itemDatabaseId ? " ⚠" : ""}
                        </span>
                      </div>
                    );
                  })}
                  {!(sel.items || []).length && (
                    <p className="p-4 text-center text-xs text-gray-500">
                      No items.
                    </p>
                  )}
                </div>
                {sel.notes && (
                  <p className="rounded-lg bg-amber-500/5 px-3 py-2 text-[12px] text-amber-200/80">
                    Scope notes: {sel.notes}
                  </p>
                )}

                {/* import */}
                <div className="rounded-xl border border-orange-500/30 bg-orange-500/[0.04] p-3">
                  <div className="mb-2 text-xs font-semibold text-orange-200">
                    Bring it into the estimate
                  </div>
                  <input
                    value={tq}
                    onChange={(e) => setTq(e.target.value)}
                    placeholder="Find assemblies…"
                    className={cls(inp2, "mb-2")}
                  />
                  <div className="mb-2 flex gap-3 text-[11px]">
                    <button
                      onClick={() =>
                        setTargets(new Set(tShown.map((c) => c.id)))
                      }
                      className="text-orange-300 hover:underline"
                    >
                      Select all shown
                    </button>
                    <button
                      onClick={() => setTargets(new Set())}
                      className="text-gray-400 hover:underline"
                    >
                      Clear
                    </button>
                    <span className="ml-auto text-gray-500">
                      {targets.size} selected
                    </span>
                  </div>
                  <div className="grid max-h-44 grid-cols-1 gap-x-4 overflow-y-auto sm:grid-cols-2">
                    {tShown.map((c) => (
                      <label
                        key={c.id}
                        className="flex cursor-pointer items-center gap-2 py-0.5 text-[12.5px] text-gray-200"
                      >
                        <input
                          type="checkbox"
                          checked={targets.has(c.id)}
                          onChange={(e) =>
                            setTargets((s) => {
                              const n = new Set(s);
                              e.target.checked ? n.add(c.id) : n.delete(c.id);
                              return n;
                            })
                          }
                          className="accent-orange-500"
                        />
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: c.color }}
                        />
                        <span className="truncate">{c.name}</span>
                        {c.itemCount > 0 && (
                          <span className="shrink-0 text-[10px] text-gray-500">
                            {c.itemCount} items
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-white/10 pt-3 text-[12px] text-gray-300">
                    <label className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        checked={mode === "append"}
                        onChange={() => setMode("append")}
                        className="accent-orange-500"
                      />
                      Add to the items already there
                    </label>
                    <label className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        checked={mode === "replace"}
                        onChange={() => setMode("replace")}
                        className="accent-orange-500"
                      />
                      Replace the items
                    </label>
                    <label className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={refresh}
                        onChange={(e) => setRefresh(e.target.checked)}
                        className="accent-orange-500"
                      />
                      Use today's catalog prices
                    </label>
                    <button
                      onClick={doImport}
                      disabled={!targets.size}
                      className="ml-auto rounded-lg bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                    >
                      Import into {targets.size || ""} assembl
                      {targets.size === 1 ? "y" : "ies"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">
                Pick a standard assembly on the left — or build a new one.
              </div>
            )}
          </div>
        </div>
      </Modal>
      <ItemSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={addFromCatalog}
        catalog={catalog}
        loading={catLoading}
        title="Add to the standard assembly"
        sub={draft?.name || ""}
        z="z-[2100]"
      />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PRINT
// ═════════════════════════════════════════════════════════════════════════════
export function PrintModal({
  open,
  onClose,
  print = {},
  onChange,
  onPrint,
  onPrintDrawings = null, // (pages[]) => void — working drawings for site (one PDF, each sheet at its own size)
  defaults = {},
  sheets = [], // [{ page, label }] sheets that have takeoff (for the legend)
}) {
  const [preset, setPreset] = useState(print.preset || "internal");
  const [sections, setSections] = useState(
    () => new Set(PRINT_PRESETS[print.preset || "internal"].sections),
  );
  const [showRates, setShowRates] = useState(
    PRINT_PRESETS[print.preset || "internal"].showRates,
  );
  // sheets that get a drawing picture in the legend (the rest get the table only)
  const [pics, setPics] = useState(() => new Set());
  useEffect(() => {
    if (!open) return;
    const p = print.preset || "internal";
    setPreset(p);
    setSections(new Set(print.sections?.[p] || PRINT_PRESETS[p].sections));
    setShowRates(print.showRates?.[p] ?? PRINT_PRESETS[p].showRates);
    // pictures come from the cached sheet pictures now (fast) — all sheets by default
    setPics(new Set(sheets.map((s) => s.page)));
  }, [open]); // eslint-disable-line
  const pickPreset = (p) => {
    setPreset(p);
    setSections(new Set(print.sections?.[p] || PRINT_PRESETS[p].sections));
    setShowRates(print.showRates?.[p] ?? PRINT_PRESETS[p].showRates);
  };
  const f = (k) => print[k] ?? defaults[k] ?? "";
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  const go = () => {
    const secs = PRINT_SECTIONS.map(([k]) => k).filter((k) => sections.has(k));
    onChange({
      preset,
      sections: { ...(print.sections || {}), [preset]: secs },
      showRates: { ...(print.showRates || {}), [preset]: showRates },
    });
    onPrint({
      mode: preset,
      sections: secs,
      showRates,
      legendPages: [...pics],
    });
  };
  return (
    <Modal open={open} onClose={onClose} width="max-w-3xl" top="pt-[4vh]">
      <Head
        title="Print estimate"
        sub="Opens a clean report in a new window — print it or “Save as PDF”"
        onClose={onClose}
      />
      <div className="grid max-h-[80vh] grid-cols-1 gap-5 overflow-y-auto p-4 md:grid-cols-2">
        <div className="space-y-3">
          {onPrintDrawings && sheets.length > 0 && (
            <div className="rounded-xl border border-orange-500/30 bg-orange-500/[0.06] p-3">
              <Lbl className="text-orange-300">Working drawings for site</Lbl>
              <p className="mb-2 text-[11.5px] leading-snug text-gray-300">
                Each sheet at its own paper size with the takeoff and the legend
                on it — one PDF to download, print or share.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onPrintDrawings(sheets.map((s) => s.page))}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
                >
                  All {sheets.length} sheet{sheets.length === 1 ? "" : "s"}
                </button>
                {sections.has("legend") &&
                  pics.size > 0 &&
                  pics.size < sheets.length && (
                    <button
                      onClick={() => onPrintDrawings([...pics])}
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                    >
                      The {pics.size} picked below
                    </button>
                  )}
              </div>
            </div>
          )}
          <div>
            <Lbl>Report</Lbl>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PRINT_PRESETS).map(([k, p]) => (
                <button
                  key={k}
                  onClick={() => pickPreset(k)}
                  className={cls(
                    "rounded-xl border px-3 py-2 text-left",
                    preset === k
                      ? "border-orange-500 bg-orange-500/10"
                      : "border-white/10 bg-black/20 hover:border-white/20",
                  )}
                >
                  <div className="text-[13px] font-semibold text-white">
                    {p.label}
                  </div>
                  <div className="text-[10.5px] text-gray-500">
                    {k === "internal"
                      ? "Every cost, rate, hour and the take-list"
                      : "Price, scope and terms — no unit costs"}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Lbl>Sections</Lbl>
            <div className="space-y-1">
              {PRINT_SECTIONS.map(([k, l]) => (
                <label
                  key={k}
                  className="flex cursor-pointer items-center gap-2 text-[12.5px] text-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={sections.has(k)}
                    onChange={(e) =>
                      setSections((s) => {
                        const n = new Set(s);
                        e.target.checked ? n.add(k) : n.delete(k);
                        return n;
                      })
                    }
                    className="accent-orange-500"
                  />
                  {l}
                </label>
              ))}
              <label className="flex cursor-pointer items-center gap-2 pt-1 text-[12.5px] text-gray-200">
                <input
                  type="checkbox"
                  checked={showRates}
                  onChange={(e) => setShowRates(e.target.checked)}
                  className="accent-orange-500"
                />
                Show unit rates
              </label>
            </div>
          </div>
          {sections.has("legend") && sheets.length > 0 && (
            <div>
              <Lbl>Legend sheets — drawing picture</Lbl>
              <div className="mb-1 flex gap-3 text-[11px]">
                <button
                  onClick={() => setPics(new Set(sheets.map((s) => s.page)))}
                  className="text-orange-300 hover:underline"
                >
                  All
                </button>
                <button
                  onClick={() => setPics(new Set())}
                  className="text-gray-400 hover:underline"
                >
                  None (tables only)
                </button>
                <span className="ml-auto text-gray-500">
                  {pics.size} of {sheets.length}
                </span>
              </div>
              <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-lg border border-white/10 p-2">
                {sheets.map((s) => (
                  <label
                    key={s.page}
                    className="flex cursor-pointer items-center gap-2 text-[12px] text-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={pics.has(s.page)}
                      onChange={(e) =>
                        setPics((p) => {
                          const n = new Set(p);
                          e.target.checked ? n.add(s.page) : n.delete(s.page);
                          return n;
                        })
                      }
                      className="accent-orange-500"
                    />
                    <span className="truncate">{s.label}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[10.5px] text-gray-500">
                Every sheet gets its assembly table. Pictures reuse the
                drawing's cached sheet pictures, so they're quick.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="col-span-2">
              <Lbl>Company name</Lbl>
              <input
                value={f("company")}
                onChange={set("company")}
                className={inp2}
              />
            </label>
            <label className="col-span-2">
              <Lbl>Client / GC</Lbl>
              <input
                value={f("client")}
                onChange={set("client")}
                className={inp2}
              />
            </label>
            <label>
              <Lbl>Prepared by</Lbl>
              <input
                value={f("preparedBy")}
                onChange={set("preparedBy")}
                className={inp2}
              />
            </label>
            <label>
              <Lbl>Estimate no.</Lbl>
              <input
                value={f("estimateNo")}
                onChange={set("estimateNo")}
                className={inp2}
              />
            </label>
            <label>
              <Lbl>Valid for (days)</Lbl>
              <input
                type="number"
                value={f("validDays") || 30}
                onChange={set("validDays")}
                className={inp2}
              />
            </label>
          </div>
        </div>
        <div className="space-y-3">
          <label className="block">
            <Lbl>Inclusions (one per line)</Lbl>
            <textarea
              rows={5}
              value={f("inclusions")}
              onChange={set("inclusions")}
              className={inp2}
              placeholder={
                "Supply & install steel framing and drywall as drawn\nLevel 4 finish to painted walls"
              }
            />
          </label>
          <label className="block">
            <Lbl>Exclusions (one per line)</Lbl>
            <textarea
              rows={5}
              value={f("exclusions")}
              onChange={set("exclusions")}
              className={inp2}
              placeholder={
                "Painting\nFirestopping of mechanical penetrations\nWork after hours"
              }
            />
          </label>
          <label className="block">
            <Lbl>Assumptions & qualifications</Lbl>
            <textarea
              rows={4}
              value={f("assumptions")}
              onChange={set("assumptions")}
              className={inp2}
              placeholder={"Clear access to all floors\nHoisting by others"}
            />
          </label>
          <p className="text-[10.5px] text-gray-500">
            These details are saved with this estimate.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
        <button
          onClick={onClose}
          className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
        >
          Close
        </button>
        <button
          onClick={go}
          className="rounded-lg bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
        >
          Open report & print
        </button>
      </div>
    </Modal>
  );
}

// ── pick assemblies (paste into many) ────────────────────────────────────────
export function AssemblyPicker({
  open,
  onClose,
  title,
  sub,
  conditions = [],
  onPick,
  action = "Paste",
}) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  useEffect(() => {
    if (open) {
      setQ("");
      setPicked(new Set());
    }
  }, [open]);
  const shown = conditions.filter(
    (c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()),
  );
  return (
    <Modal open={open} onClose={onClose} width="max-w-lg" top="pt-[8vh]">
      <Head title={title} sub={sub} onClose={onClose} />
      <div className="space-y-2 p-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find assemblies…"
          className={inp2}
          autoFocus
        />
        <div className="flex gap-3 text-[11px]">
          <button
            onClick={() => setPicked(new Set(shown.map((c) => c.id)))}
            className="text-orange-300 hover:underline"
          >
            Select all shown
          </button>
          <button
            onClick={() => setPicked(new Set())}
            className="text-gray-400 hover:underline"
          >
            Clear
          </button>
          <span className="ml-auto text-gray-500">{picked.size} selected</span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-white/10">
          {shown.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2 border-b border-white/5 px-3 py-1.5 text-[12.5px] text-gray-200 hover:bg-white/5"
            >
              <input
                type="checkbox"
                checked={picked.has(c.id)}
                onChange={(e) =>
                  setPicked((s) => {
                    const n = new Set(s);
                    e.target.checked ? n.add(c.id) : n.delete(c.id);
                    return n;
                  })
                }
                className="accent-orange-500"
              />
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: c.color }}
              />
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
              {c.itemCount > 0 && (
                <span className="text-[10px] text-gray-500">
                  {c.itemCount} items
                </span>
              )}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
          >
            Cancel
          </button>
          <button
            disabled={!picked.size}
            onClick={() => {
              onPick([...picked]);
              onClose();
            }}
            className="rounded-lg bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
          >
            {action} into {picked.size || ""} assembl
            {picked.size === 1 ? "y" : "ies"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── small dropdown menu ──────────────────────────────────────────────────────
export function Menu({ label, items, className }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div ref={ref} className={cls("relative", className)}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="rounded-lg bg-white/5 px-2.5 py-1.5 text-xs font-medium text-gray-300 hover:bg-white/10"
      >
        {label}
      </button>
      {open && (
        <div className="absolute right-0 bottom-full z-30 mb-1 min-w-[200px] overflow-hidden rounded-xl border border-white/10 bg-gray-900 py-1 shadow-2xl">
          {items.filter(Boolean).map(([l, fn, danger], k) => (
            <button
              key={k}
              onClick={() => {
                setOpen(false);
                fn();
              }}
              className={cls(
                "block w-full px-3 py-1.5 text-left text-[12.5px] hover:bg-white/5",
                danger ? "text-red-300" : "text-gray-200",
              )}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
